import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import CareerFormations from '../components/CareerFormations';
import { motion } from 'framer-motion';
import { 
  Download, Mail, RefreshCw, Home, Image, FileText, Check, ExternalLink,
  Heart, Users, Stethoscope, Palette, Monitor, Wrench, FlaskConical, Rocket,
  ChevronRight, Sparkles, Share2, Linkedin, Facebook, Twitter, GraduationCap
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_vocation-guide-1/artifacts/og76lhbc_logo%20neutre.%20.png";

const domainIcons = {
  social: Users,
  education: Heart,
  health: Stethoscope,
  creativity: Palette,
  technology: Monitor,
  technical: Wrench,
  science: FlaskConical,
  business: Rocket,
  administration: FileText,
  sports: Heart
};

const domainColors = {
  social: '#FB6823',
  education: '#A4187A',
  health: '#00C853',
  creativity: '#E91E63',
  technology: '#2196F3',
  technical: '#795548',
  science: '#009688',
  business: '#FF5722',
  administration: '#607D8B',
  sports: '#4CAF50'
};

// Short labels for radar chart to avoid text truncation
const domainShortLabels = {
  fr: {
    social: "Social",
    education: "Enseign.",
    health: "Santé",
    creativity: "Créativité",
    technology: "Tech",
    technical: "Technique",
    science: "Science",
    business: "Business",
    administration: "Gestion",
    sports: "Sport"
  },
  nl: {
    social: "Sociaal",
    education: "Onderwijs",
    health: "Gezondheid",
    creativity: "Creatief",
    technology: "Tech",
    technical: "Technisch",
    science: "Wetenschap",
    business: "Business",
    administration: "Beheer",
    sports: "Sport"
  }
};

export default function Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedCareers, setSelectedCareers] = useState([]);
  const resultsRef = useRef(null);

  const result = location.state?.result;
  const sessionId = location.state?.sessionId;

  if (!result) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-semibold text-[#1D1D1D] mb-4">No results found</p>
          <button
            onClick={() => navigate('/questionnaire')}
            className="neo-button-primary"
          >
            {t('retakeTest')}
          </button>
        </div>
      </div>
    );
  }

  const chartData = Object.entries(result.scores).map(([domain, score]) => ({
    domain: domainShortLabels[language]?.[domain] || t(`domains.${domain}`),
    fullDomain: t(`domains.${domain}`),
    score: Math.min(score, 100), // Cap score at 100% for display
    fullMark: 100
  }));

  // Sort scores for display
  const sortedScores = Object.entries(result.scores)
    .sort((a, b) => b[1] - a[1]);

  // Limit careers to 6 maximum
  const displayedCareers = result.careers.slice(0, 6);

  // Handle career selection for Stage 2
  const handleCareerSelect = (careerName) => {
    setSelectedCareers(prev => {
      if (prev.includes(careerName)) {
        return prev.filter(c => c !== careerName);
      }
      if (prev.length >= 3) {
        return prev; // Max 3 selections
      }
      return [...prev, careerName];
    });
  };

  // Navigate to Stage 2
  const handleStartStage2 = () => {
    if (selectedCareers.length >= 2 && selectedCareers.length <= 3) {
      navigate('/stage2', {
        state: {
          selectedCareers,
          originalResult: result,
          sessionId
        }
      });
    }
  };

  const handleSaveResults = async () => {
    if (!email) return;
    
    setSaving(true);
    try {
      // Send results by email with PDF attachment
      await axios.post(`${API}/send-results`, {
        email,
        scores: result.scores,
        careers: result.careers,
        language: language,
        include_pdf: true
      });
      setSaved(true);
    } catch (error) {
      console.error('Error sending results:', error);
      alert(language === 'fr' 
        ? 'Erreur lors de l\'envoi de l\'email. Veuillez réessayer.' 
        : 'Fout bij het verzenden van de e-mail. Probeer het opnieuw.');
    }
    setSaving(false);
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Call server-side PDF generation
      const response = await axios.post(`${API}/export/pdf`, {
        scores: result.scores,
        careers: result.careers,
        language: language
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = language === 'fr' ? 'ikigai-declic-resultats.pdf' : 'ikigai-declic-resultaten.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(language === 'fr' 
        ? 'Erreur lors de la génération du PDF. Veuillez réessayer.' 
        : 'Fout bij het genereren van de PDF. Probeer het opnieuw.');
    }
    setDownloading(false);
  };

  const handleDownloadImage = async () => {
    setDownloading(true);
    try {
      // Call server-side image generation
      const response = await axios.post(`${API}/export/image`, {
        scores: result.scores,
        careers: result.careers,
        language: language
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = language === 'fr' ? 'ikigai-declic-resultats.png' : 'ikigai-declic-resultaten.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating image:', error);
      alert(language === 'fr'
        ? 'Erreur lors de la génération de l\'image. Veuillez réessayer.'
        : 'Fout bij het genereren van de afbeelding. Probeer het opnieuw.');
    }
    setDownloading(false);
  };

  // Social sharing functions
  const shareText = language === 'fr' 
    ? `🎯 J'ai découvert mon profil Ikigai avec le Guide Ikigai Déclic ! Mes domaines forts: ${Object.entries(result.scores).sort((a,b) => b[1]-a[1]).slice(0,3).map(([d]) => t(`domains.${d}`)).join(', ')}. Découvre le tien sur`
    : `🎯 Ik heb mijn Ikigai-profiel ontdekt met de Ikigai Gids Déclic! Mijn sterke domeinen: ${Object.entries(result.scores).sort((a,b) => b[1]-a[1]).slice(0,3).map(([d]) => t(`domains.${d}`)).join(', ')}. Ontdek de jouwe op`;
  
  const shareUrl = 'https://www.declicbelgium.be';

  const handleShareLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  const handleShareFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=600');
  };

  const handleShareTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=600');
  };

  const handleCopyLink = async () => {
    const fullText = `${shareText} ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(fullText);
      alert(language === 'fr' ? 'Lien copié !' : 'Link gekopieerd!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
      <Header />
      
      <main className="flex-1 px-6 md:px-12 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Results Content for Export */}
          <div ref={resultsRef} className="bg-[#EBEBEB] p-4 md:p-8">
            {/* Header with Logo */}
            <div className="flex items-center justify-center mb-8">
              <img 
                src={LOGO_URL} 
                alt="Déclic Logo" 
                className="h-16 w-auto"
              />
            </div>

            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-black text-center text-[#1D1D1D] mb-8"
              data-testid="results-title"
            >
              {t('yourResults')}
            </motion.h1>

            {/* Radar Chart */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="neo-card mb-8"
              data-testid="radar-chart-container"
            >
              <h2 className="text-xl font-bold text-[#1D1D1D] mb-4 text-center">
                {t('domainScores')}
              </h2>
              <div className="h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={chartData} outerRadius="65%">
                    <PolarGrid stroke="#E0E0E0" />
                    <PolarAngleAxis 
                      dataKey="domain" 
                      tick={({ x, y, payload, textAnchor, index }) => (
                        <text
                          x={x}
                          y={y}
                          textAnchor={textAnchor}
                          fill="#1D1D1D"
                          fontSize={11}
                          fontWeight="500"
                          dominantBaseline="central"
                        >
                          {payload.value}
                        </text>
                      )}
                    />
                    <PolarRadiusAxis 
                      angle={30} 
                      domain={[0, 100]} 
                      tick={{ fill: '#666', fontSize: 10 }}
                    />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke="#A4187A"
                      fill="#FB6823"
                      fillOpacity={0.4}
                      strokeWidth={2}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 rounded-xl border-2 border-[#1D1D1D] shadow-lg">
                              <p className="font-bold text-[#1D1D1D]">{data.fullDomain}</p>
                              <p className="text-[#A4187A] font-semibold">{Math.min(data.score, 100).toFixed(0)}%</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Score Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 mb-8 overflow-x-auto"
              data-testid="score-cards"
            >
              {sortedScores.map(([domain, score], index) => {
                const Icon = domainIcons[domain];
                const color = domainColors[domain];
                const displayScore = Math.min(score, 100); // Cap at 100%
                return (
                  <div
                    key={domain}
                    className="result-domain-card min-w-0"
                    style={{ 
                      boxShadow: `3px 3px 0px 0px ${color}`,
                      animationDelay: `${index * 0.1}s`
                    }}
                    data-testid={`score-${domain}`}
                  >
                    <div className="flex items-center gap-1 md:gap-2 mb-2">
                      <div 
                        className="w-6 h-6 md:w-8 md:h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        <Icon className="w-3 h-3 md:w-4 md:h-4 text-white" />
                      </div>
                      <span className="font-bold text-[#1D1D1D] text-xs md:text-sm truncate">
                        {t(`domains.${domain}`)}
                      </span>
                    </div>
                    <div className="text-xl md:text-2xl font-black" style={{ color }}>
                      {displayScore.toFixed(0)}%
                    </div>
                  </div>
                );
              })}
            </motion.div>

            {/* Career Suggestions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="neo-card"
              data-testid="career-suggestions"
            >
              {/* Stage 2 Introduction - BEFORE career list */}
              <div className="mb-6">
                {/* Step Progress Indicator */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#A4187A] text-white flex items-center justify-center text-sm font-bold">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-[#A4187A]">
                      {language === 'fr' ? 'Étape 1' : 'Stap 1'}
                    </span>
                  </div>
                  <div className="flex-1 max-w-[60px] h-1 bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-full"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#FB6823] text-white flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    <span className="text-sm font-medium text-[#FB6823]">
                      {language === 'fr' ? 'Étape 2' : 'Stap 2'}
                    </span>
                  </div>
                </div>
                
                {/* Transition Message */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-xl p-5"
                >
                  <div className="text-center mb-3">
                    <span className="text-2xl">{t('stepIndicator.step1Complete')}</span>
                  </div>
                  <p className="text-center text-[#1D1D1D] font-medium mb-4">
                    {t('stepIndicator.step2Invitation')}
                  </p>
                  
                  {/* Benefits List */}
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-[#A4187A] mb-2">
                      {t('stepIndicator.step2Benefits')}
                    </p>
                    <ul className="space-y-2 text-sm text-[#1D1D1D]/80">
                      <li className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-[#FB6823]/20 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-[#FB6823]" />
                        </div>
                        {t('stepIndicator.benefit1')}
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-[#FB6823]/20 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-[#FB6823]" />
                        </div>
                        {t('stepIndicator.benefit2')}
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-[#FB6823]/20 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-[#FB6823]" />
                        </div>
                        {t('stepIndicator.benefit3')}
                      </li>
                    </ul>
                  </div>
                  
                  {/* Full Results Message */}
                  <p className="text-center text-xs text-[#A4187A]/70 italic">
                    💡 {t('stepIndicator.fullResultsMessage')}
                  </p>
                </motion.div>
              </div>

              {/* Career Selection Header */}
              <div className="border-t-2 border-dashed border-[#E0E0E0] pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h2 className="text-xl font-bold text-[#1D1D1D]">
                    {t('careerSuggestions')}
                  </h2>
                  {/* Stage 2 Selection Info */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#1D1D1D]/60">
                      {t('stage2.selectCareers')}
                    </span>
                    <span className={`px-2 py-1 rounded-full font-medium ${
                      selectedCareers.length >= 2 
                        ? 'bg-[#A4187A] text-white' 
                        : 'bg-[#F3E5F5] text-[#A4187A]'
                    }`}>
                      {selectedCareers.length}/3 {t('stage2.selectedCount')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {displayedCareers.map((career, index) => {
                  // Get primary domain for icon/color
                  const primaryDomain = career.domains?.[0] || career.domain || 'social';
                  const Icon = domainIcons[primaryDomain] || Users;
                  const color = domainColors[primaryDomain] || '#A4187A';
                  const isSelected = selectedCareers.includes(career.name);
                  const canSelect = selectedCareers.length < 3 || isSelected;
                  
                  return (
                    <div
                      key={index}
                      onClick={() => canSelect && handleCareerSelect(career.name)}
                      className={`neo-card-interactive cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? 'ring-2 ring-[#A4187A] bg-[#F3E5F5]/50' 
                          : canSelect 
                            ? 'hover:ring-2 hover:ring-[#FB6823]/50' 
                            : 'opacity-60 cursor-not-allowed'
                      }`}
                      data-testid={`career-${index}`}
                    >
                      {/* Mobile: Stack layout, Desktop: Row layout */}
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                        {/* Top row on mobile: checkbox + icon + match */}
                        <div className="flex items-center gap-3 sm:contents">
                          {/* Selection Checkbox */}
                          <div 
                            className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              isSelected 
                                ? 'border-[#A4187A] bg-[#A4187A]' 
                                : 'border-[#E0E0E0]'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                          
                          {/* Domain Icon */}
                          <div 
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: color }}
                          >
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          
                          {/* Match percentage - visible on mobile in top row */}
                          <span 
                            className="ml-auto sm:hidden text-xs font-semibold px-2 py-1 rounded-full"
                            style={{ backgroundColor: `${color}20`, color }}
                          >
                            {career.match}
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-[#1D1D1D] text-sm sm:text-base mb-1 leading-tight">
                            {career.name}
                          </h3>
                          <p className="text-xs sm:text-sm text-[#1D1D1D]/60 mb-2 line-clamp-2">
                            {career.description}
                          </p>
                          {career.work_environment && (
                            <p className="text-xs text-[#1D1D1D]/50 mb-2 hidden sm:block">
                              📍 {career.work_environment}
                            </p>
                          )}
                          {career.skills && career.skills.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {career.skills.slice(0, 2).map((skill, i) => (
                                <span 
                                  key={i}
                                  className="text-xs px-2 py-0.5 rounded-full bg-[#F3E5F5] text-[#A4187A]"
                                >
                                  {skill}
                                </span>
                              ))}
                              {career.skills.length > 2 && (
                                <span className="text-xs text-[#1D1D1D]/40">
                                  +{career.skills.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Match percentage - hidden on mobile, visible on desktop */}
                          <span 
                            className="hidden sm:inline-block text-xs font-semibold px-2 py-1 rounded-full"
                            style={{ backgroundColor: `${color}20`, color }}
                          >
                            {t('match')}: {career.match}
                          </span>
                          
                          {/* Formations section */}
                          <CareerFormations 
                            careerName={career.name} 
                            language={language} 
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Stage 2 CTA Button */}
              <div className="border-t-2 border-dashed border-[#E0E0E0] pt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-center sm:text-left">
                    <p className="text-sm text-[#1D1D1D]/70">
                      {selectedCareers.length < 2 
                        ? t('stage2.minSelection')
                        : `${selectedCareers.length} ${t('stage2.selectedCount')} - ${t('stage2.duration')}`
                      }
                    </p>
                  </div>
                  <button
                    onClick={handleStartStage2}
                    disabled={selectedCareers.length < 2}
                    className={`neo-button-primary flex items-center gap-2 text-lg py-4 px-8 ${
                      selectedCareers.length < 2 ? 'opacity-50 cursor-not-allowed' : 'animate-pulse'
                    }`}
                    data-testid="start-stage2-btn"
                  >
                    <Sparkles className="w-5 h-5" />
                    {t('stepIndicator.continueToStep2')}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Actions Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 space-y-6"
          >
            {/* Save Results */}
            <div className="neo-card">
              <h3 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {t('saveResults')}
              </h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="neo-input flex-1"
                  disabled={saved}
                  data-testid="email-input"
                />
                <button
                  onClick={handleSaveResults}
                  disabled={!email || saving || saved}
                  className={`neo-button-primary flex items-center justify-center gap-2 ${
                    saved ? 'bg-[#00C853]' : ''
                  } ${(!email || saving) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="save-btn"
                >
                  {saved ? (
                    <>
                      <Check className="w-5 h-5" />
                      {t('savedSuccess')}
                    </>
                  ) : (
                    <>
                      {saving ? t('loading') : t('save')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Download Options */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="neo-button-secondary flex items-center justify-center gap-2 flex-1"
                data-testid="download-pdf-btn"
              >
                <FileText className="w-5 h-5" />
                {t('downloadPdf')}
              </button>
              <button
                onClick={handleDownloadImage}
                disabled={downloading}
                className="neo-button-outline flex items-center justify-center gap-2 flex-1"
                data-testid="download-image-btn"
              >
                <Image className="w-5 h-5" />
                {t('downloadImage')}
              </button>
            </div>

            {/* PART 4: Encouragement & Meet Professionals CTA */}
            <div className="neo-card bg-gradient-to-br from-[#FB6823]/10 via-white to-[#A4187A]/10 border-2 border-[#FB6823]/30">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#FB6823] to-[#A4187A] mb-4">
                  <span className="text-2xl">🌟</span>
                </div>
                <h3 className="text-xl font-bold text-[#1D1D1D] mb-2">
                  {language === 'fr' 
                    ? 'Continue ton exploration !' 
                    : 'Ga verder met je verkenning!'}
                </h3>
                <p className="text-[#1D1D1D]/70 max-w-md mx-auto">
                  {language === 'fr' 
                    ? "Ce test t'a ouvert des pistes. Maintenant, il est temps de les approfondir en rencontrant des professionnels qui vivent ces métiers au quotidien."
                    : "Deze test heeft je nieuwe mogelijkheden geopend. Nu is het tijd om ze te verdiepen door professionals te ontmoeten die deze beroepen dagelijks uitoefenen."}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Meet Professionals */}
                <div className="bg-white rounded-xl p-5 border border-[#FB6823]/20 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FB6823]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">🤝</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1D1D1D] mb-1">
                        {language === 'fr' ? 'Rencontre des professionnels' : 'Ontmoet professionals'}
                      </h4>
                      <p className="text-sm text-[#1D1D1D]/60 mb-3">
                        {language === 'fr' 
                          ? 'Participe aux événements Déclic pour échanger avec des professionnels de tes métiers favoris.'
                          : 'Neem deel aan Déclic-evenementen om te praten met professionals uit jouw favoriete beroepen.'}
                      </p>
                      <a 
                        href="https://www.declicbelgium.be/journee-declic" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#FB6823] hover:text-[#E55A1B] transition-colors"
                      >
                        {language === 'fr' ? 'Découvrir les Journées Déclic' : 'Ontdek de Déclic-dagen'}
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Déclic Workshops */}
                <div className="bg-white rounded-xl p-5 border border-[#A4187A]/20 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#A4187A]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">🎯</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1D1D1D] mb-1">
                        {language === 'fr' ? 'Ateliers Déclic@School' : 'Déclic@School workshops'}
                      </h4>
                      <p className="text-sm text-[#1D1D1D]/60 mb-3">
                        {language === 'fr' 
                          ? "Demande à ton école d'organiser un atelier Déclic pour approfondir ton orientation."
                          : "Vraag je school om een Déclic-workshop te organiseren om je oriëntatie te verdiepen."}
                      </p>
                      <a 
                        href="https://www.declicbelgium.be/declic-school" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#A4187A] hover:text-[#8B1469] transition-colors"
                      >
                        {language === 'fr' ? 'En savoir plus' : 'Meer informatie'}
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Déclic branding */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <p className="text-xs text-[#1D1D1D]/50">
                  {language === 'fr' 
                    ? 'Test réalisé sur ' 
                    : 'Test uitgevoerd op '}
                  <a 
                    href="https://www.declicbelgium.be" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#A4187A] hover:underline font-medium"
                  >
                    www.declicbelgium.be
                  </a>
                </p>
              </div>
            </div>

            {/* Explore Formations CTA */}
            <div className="neo-card bg-gradient-to-r from-[#A4187A]/5 to-[#FB6823]/5 border-2 border-[#A4187A]/20">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#A4187A]/10 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-[#A4187A]" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-bold text-[#1D1D1D]">
                    {language === 'fr' ? 'Découvre les formations' : 'Ontdek de opleidingen'}
                  </h3>
                  <p className="text-sm text-[#1D1D1D]/60">
                    {language === 'fr' 
                      ? 'Explore les écoles et formations belges liées à tes métiers suggérés'
                      : 'Verken Belgische scholen en opleidingen gerelateerd aan jouw voorgestelde beroepen'}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/formations?lang=${language}`)}
                  className="neo-button-primary flex items-center gap-2 whitespace-nowrap"
                  data-testid="explore-formations-btn"
                >
                  <GraduationCap className="w-5 h-5" />
                  {language === 'fr' ? 'Explorer' : 'Verkennen'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Social Sharing */}
            <div className="neo-card">
              <h3 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                {language === 'fr' ? 'Partager mes résultats' : 'Mijn resultaten delen'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {language === 'fr' 
                  ? 'Partage ton profil Ikigai avec tes amis et ta famille !'
                  : 'Deel je Ikigai-profiel met je vrienden en familie!'}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleShareLinkedIn}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-lg hover:bg-[#006097] transition-colors"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                  LinkedIn
                </button>
                <button
                  onClick={handleShareFacebook}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#1565C0] transition-colors"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-5 h-5" />
                  Facebook
                </button>
                <button
                  onClick={handleShareTwitter}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] text-white rounded-lg hover:bg-[#1A91DA] transition-colors"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-5 h-5" />
                  Twitter
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  data-testid="share-copy"
                >
                  <ExternalLink className="w-5 h-5" />
                  {language === 'fr' ? 'Copier' : 'Kopiëren'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Guide Ikigai Déclic - www.declicbelgium.be
              </p>
            </div>

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/questionnaire')}
                className="neo-button-outline flex items-center justify-center gap-2 flex-1"
                data-testid="retake-btn"
              >
                <RefreshCw className="w-5 h-5" />
                {t('retakeTest')}
              </button>
              <button
                onClick={() => navigate('/')}
                className="neo-button-outline flex items-center justify-center gap-2 flex-1"
                data-testid="home-btn"
              >
                <Home className="w-5 h-5" />
                {t('backHome')}
              </button>
            </div>

            {/* Back to Déclic Website */}
            <div className="mt-4">
              <a
                href="https://www.declicbelgium.be"
                target="_blank"
                rel="noopener noreferrer"
                className="neo-button-primary flex items-center justify-center gap-2 w-full"
                data-testid="declic-website-btn"
              >
                <ExternalLink className="w-5 h-5" />
                {t('backToDeclic')}
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
